/**
 * Pan/zoom/rotate photo cropper with a fixed template frame — the step
 * between taking/uploading a photo and running recognition on it. Crops out
 * background clutter (wall, discards, other players) before either
 * recognition engine ever sees the photo.
 *
 * The whole photo stays visible in the stage, dimmed outside a frame
 * overlay in the middle — the operator drags (or pinches/uses +/-) the
 * photo underneath until their tiles line up inside the frame, while
 * still being able to see the rest of the table for context. An earlier
 * version made the frame itself the entire visible viewport, which made
 * it hard to tell where the frame sat relative to the whole photo.
 *
 * Rotation has two controls, both because a phone photo can be off in two
 * different ways: a full quarter-turn (portrait/upside-down shots — snap
 * buttons, exact) and a few degrees of tilt from an unsquare hand-held shot
 * (a "straighten" slider, since a two-finger twist gesture would fight the
 * pinch-to-zoom gesture using the same two fingers). The two compose: total
 * rotation is the 90-degree snap plus the slider's fine offset.
 *
 * The photo is tracked by its own centre point, not a corner — the natural
 * pivot for combining rotation with pan/zoom, since rotating around a
 * corner would fling the image sideways instead of turning it in place.
 *
 * The frame is never allowed to show anything but photo. At any rotation,
 * "does the photo fully cover the frame" is an exact test (all four frame
 * corners fall inside the photo's own rectangle, checked in the photo's
 * unrotated coordinate space) — used both to set the minimum zoom for the
 * current rotation and to reject drags/zooms that would leave a gap.
 *
 * One frame shape, no layout choice to get wrong. Earlier this offered a
 * same-line/bonus-above/bonus-below picker with a frame shaped to match,
 * but that put a real recognition-accuracy risk in the operator's hands for
 * no benefit: recognition (verified directly against both engines) reads
 * tiles wherever they are in frame and classifies by tile type, not by row
 * position, so the frame's shape was never actually informing recognition
 * — it only needs to be generous enough to fit whatever's really there.
 */

import { renderBrandHeader } from '../branding';

/** Logical stage units (not CSS pixels — scaled responsively by the container). */
const STAGE_WIDTH = 900;
const STAGE_HEIGHT = 700;

/** The crop frame, centred in the stage — tall enough for two rows of tiles. */
const FRAME_WIDTH = 820;
const FRAME_HEIGHT = 260;
const FRAME_X = (STAGE_WIDTH - FRAME_WIDTH) / 2;
const FRAME_Y = (STAGE_HEIGHT - FRAME_HEIGHT) / 2;
const FRAME_CENTER_X = FRAME_X + FRAME_WIDTH / 2;
const FRAME_CENTER_Y = FRAME_Y + FRAME_HEIGHT / 2;

const MAX_ZOOM_FACTOR = 5;
const FINE_ROTATE_MAX_DEG = 25;
/** Slack added to the exact coverage test so float rounding never rejects a genuinely valid position. */
const COVERAGE_EPSILON = 0.01;

export interface PhotoCropActions {
  confirm(croppedPhoto: string): void;
  cancel(): void;
}

interface PointerState {
  x: number;
  y: number;
}

export function renderPhotoCrop(
  root: HTMLElement,
  photo: string,
  actions: PhotoCropActions,
): void {
  // Photo centre position within the stage, and its scale — both in logical
  // stage units. Rotation is tracked separately as degrees (0/90/180/270
  // from the snap buttons, plus a fine offset from the straighten slider).
  let scale = 1;
  let centerX = FRAME_CENTER_X;
  let centerY = FRAME_CENTER_Y;
  let photoNaturalW = 0;
  let photoNaturalH = 0;
  let baseRotation = 0;
  let fineRotation = 0;

  const pointers = new Map<number, PointerState>();
  let dragStart: { centerX: number; centerY: number; x: number; y: number } | undefined;
  let pinchStartDistance = 0;
  let pinchStartScale = 1;

  const img = new Image();

  function totalRotationDeg(): number {
    return baseRotation + fineRotation;
  }

  /**
   * Whether the photo (centred at cx,cy, at this scale and rotation) still
   * fully covers the frame — exact test, not an approximation. Maps each
   * frame corner into the photo's own unrotated coordinate space (inverse
   * of the display transform) and checks it falls within the photo's
   * natural bounds; since both shapes are rectangles (hence convex), all
   * four corners landing inside is necessary and sufficient for the whole
   * frame to be covered.
   */
  function coversFrame(cx: number, cy: number, s: number, rotDeg: number): boolean {
    const rad = (rotDeg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const halfW = photoNaturalW / 2 + COVERAGE_EPSILON;
    const halfH = photoNaturalH / 2 + COVERAGE_EPSILON;
    const corners: Array<[number, number]> = [
      [FRAME_X, FRAME_Y],
      [FRAME_X + FRAME_WIDTH, FRAME_Y],
      [FRAME_X, FRAME_Y + FRAME_HEIGHT],
      [FRAME_X + FRAME_WIDTH, FRAME_Y + FRAME_HEIGHT],
    ];
    return corners.every(([fx, fy]) => {
      const dx = fx - cx;
      const dy = fy - cy;
      const u = (dx * cos + dy * sin) / s;
      const v = (-dx * sin + dy * cos) / s;
      return Math.abs(u) <= halfW && Math.abs(v) <= halfH;
    });
  }

  /**
   * The smallest scale at which the photo, centred on the frame, still
   * covers it at the current rotation — exact for the centred case (the
   * frame's own bounding box, rotated into the photo's frame, must fit
   * within the photo's natural size).
   */
  function minScale(): number {
    const rad = (totalRotationDeg() * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    const neededW = FRAME_WIDTH * cos + FRAME_HEIGHT * sin;
    const neededH = FRAME_WIDTH * sin + FRAME_HEIGHT * cos;
    return Math.max(neededW / photoNaturalW, neededH / photoNaturalH);
  }

  function resetFraming(): void {
    scale = minScale();
    centerX = FRAME_CENTER_X;
    centerY = FRAME_CENTER_Y;
  }

  /**
   * Re-validates the current scale/position after a rotation change.
   * Preserves the operator's existing zoom/pan whenever it's still valid
   * (the common case for a small straighten adjustment) and only snaps
   * back to a centred fit when the new rotation genuinely doesn't fit —
   * a full re-derivation of the ideal pan for every rotation isn't worth
   * it for what's meant to be a small correction, not a redo.
   */
  function ensureValidFraming(): void {
    const min = minScale();
    if (scale < min) scale = min;
    if (!coversFrame(centerX, centerY, scale, totalRotationDeg())) {
      centerX = FRAME_CENTER_X;
      centerY = FRAME_CENTER_Y;
    }
  }

  /** Real CSS pixels per logical stage unit, at the container's current responsive width. */
  function pxPerUnit(): number {
    return stageEl.getBoundingClientRect().width / STAGE_WIDTH;
  }

  function applyTransform(): void {
    const el = root.querySelector<HTMLElement>('#cropPhoto');
    if (!el) return;
    // centerX/Y and scale are tracked in logical stage units throughout —
    // only here, at the point of actually painting the transform, do they
    // need converting to the real CSS pixels the stage is rendered at
    // (which shrinks/grows with the responsive container width). The image
    // element sits at left:0/top:0 with its natural intrinsic size, so
    // positioning its *centre* at (centerX, centerY) means translating by
    // centre-minus-half-its-own-size, then rotating/scaling around that
    // same centre (transform-origin: center, the default).
    const ratio = pxPerUnit();
    const tx = centerX * ratio - photoNaturalW / 2;
    const ty = centerY * ratio - photoNaturalH / 2;
    el.style.transform = `translate(${tx}px, ${ty}px) rotate(${totalRotationDeg()}deg) scale(${scale * ratio})`;
  }

  function setZoom(newScale: number): void {
    const min = minScale();
    const clamped = Math.min(Math.max(newScale, min), min * MAX_ZOOM_FACTOR);
    const rot = totalRotationDeg();
    const rad = (rot * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    // Keep the frame's centre point fixed under the photo while zooming,
    // rather than zooming from the photo's own centre.
    const dx = FRAME_CENTER_X - centerX;
    const dy = FRAME_CENTER_Y - centerY;
    const u = (dx * cos + dy * sin) / scale;
    const v = (-dx * sin + dy * cos) / scale;
    const newCenterX = FRAME_CENTER_X - clamped * (u * cos - v * sin);
    const newCenterY = FRAME_CENTER_Y - clamped * (u * sin + v * cos);
    scale = clamped;
    if (coversFrame(newCenterX, newCenterY, scale, rot)) {
      centerX = newCenterX;
      centerY = newCenterY;
    } else {
      // Only reachable from float-precision edge cases at the coverage
      // boundary — fall back to the always-valid centred position.
      centerX = FRAME_CENTER_X;
      centerY = FRAME_CENTER_Y;
    }
    applyTransform();
  }

  function rotateBy90(deltaDeg: 90 | -90): void {
    baseRotation = (((baseRotation + deltaDeg) % 360) + 360) % 360;
    ensureValidFraming();
    applyTransform();
  }

  function setFineRotation(deg: number): void {
    fineRotation = Math.min(Math.max(deg, -FINE_ROTATE_MAX_DEG), FINE_ROTATE_MAX_DEG);
    ensureValidFraming();
    applyTransform();
  }

  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Position the Hand</h1>
      <p class="muted">
        Drag to slide the photo, pinch or use +/- to zoom, until the frame holds just the tiles —
        nothing else on the table. The rest of the photo stays visible around the frame so you can
        check it lines up. Rotate for a sideways/upside-down photo, or straighten a tilted one.
        Flower/season tiles can go anywhere in the frame: same row as the hand, or on a row above or
        below it — however you laid them out is fine.
      </p>

      <div id="cropStage" class="crop-stage" style="aspect-ratio:${STAGE_WIDTH} / ${STAGE_HEIGHT}">
        <img id="cropPhoto" class="crop-photo" src="${photo}" alt="Photo being positioned" draggable="false" />
        <div
          class="crop-frame-overlay"
          style="
            left:${(FRAME_X / STAGE_WIDTH) * 100}%;
            top:${(FRAME_Y / STAGE_HEIGHT) * 100}%;
            width:${(FRAME_WIDTH / STAGE_WIDTH) * 100}%;
            height:${(FRAME_HEIGHT / STAGE_HEIGHT) * 100}%;
          "
        ></div>
      </div>

      <div class="row" style="margin-top:0.5rem;justify-content:center">
        <button type="button" id="zoomOutBtn" class="secondary">−</button>
        <button type="button" id="zoomInBtn" class="secondary">+</button>
        <button type="button" id="rotateLeftBtn" class="secondary" aria-label="Rotate left 90 degrees">↺</button>
        <button type="button" id="rotateRightBtn" class="secondary" aria-label="Rotate right 90 degrees">↻</button>
      </div>

      <div class="row" style="margin-top:0.75rem;align-items:center;gap:0.5rem">
        <label for="fineRotateInput" class="muted" style="white-space:nowrap">Straighten</label>
        <input
          type="range"
          id="fineRotateInput"
          min="${-FINE_ROTATE_MAX_DEG}"
          max="${FINE_ROTATE_MAX_DEG}"
          step="0.5"
          value="0"
          style="flex:1"
        />
        <span id="fineRotateValue" class="muted" style="min-width:3.5em;text-align:right">0°</span>
      </div>

      <div class="row" style="margin-top:1rem">
        <button type="button" id="cropCancelBtn" class="secondary">← Retake</button>
        <button type="button" id="cropConfirmBtn">Use This Photo</button>
      </div>
    </div>
  `;

  const stageEl = root.querySelector<HTMLElement>('#cropStage')!;
  const photoEl = root.querySelector<HTMLImageElement>('#cropPhoto')!;
  const fineRotateInput = root.querySelector<HTMLInputElement>('#fineRotateInput')!;
  const fineRotateValue = root.querySelector<HTMLElement>('#fineRotateValue')!;

  img.onload = () => {
    photoNaturalW = img.naturalWidth;
    photoNaturalH = img.naturalHeight;
    resetFraming();
    applyTransform();
  };
  img.src = photo;

  root.querySelector<HTMLButtonElement>('#zoomInBtn')!.addEventListener('click', () => setZoom(scale * 1.25));
  root.querySelector<HTMLButtonElement>('#zoomOutBtn')!.addEventListener('click', () => setZoom(scale / 1.25));
  root.querySelector<HTMLButtonElement>('#rotateLeftBtn')!.addEventListener('click', () => rotateBy90(-90));
  root.querySelector<HTMLButtonElement>('#rotateRightBtn')!.addEventListener('click', () => rotateBy90(90));

  fineRotateInput.addEventListener('input', () => {
    const deg = Number(fineRotateInput.value);
    setFineRotation(deg);
    fineRotateValue.textContent = `${deg > 0 ? '+' : ''}${deg}°`;
  });

  function distance(a: PointerState, b: PointerState): number {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  stageEl.addEventListener('pointerdown', (e) => {
    stageEl.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 1) {
      dragStart = { centerX, centerY, x: e.clientX, y: e.clientY };
    } else if (pointers.size === 2) {
      dragStart = undefined;
      const [a, b] = [...pointers.values()] as [PointerState, PointerState];
      pinchStartDistance = distance(a, b);
      pinchStartScale = scale;
    }
  });

  stageEl.addEventListener('pointermove', (e) => {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.size >= 2) {
      const [a, b] = [...pointers.values()] as [PointerState, PointerState];
      const dist = distance(a, b);
      if (pinchStartDistance > 0) {
        setZoom(pinchStartScale * (dist / pinchStartDistance));
      }
      return;
    }

    if (!dragStart) return;
    // Screen pixels -> logical stage units, since the stage is displayed
    // scaled (responsive width) but positions are tracked in logical units.
    const rect = stageEl.getBoundingClientRect();
    const unitsPerPixel = STAGE_WIDTH / rect.width;
    const candidateX = dragStart.centerX + (e.clientX - dragStart.x) * unitsPerPixel;
    const candidateY = dragStart.centerY + (e.clientY - dragStart.y) * unitsPerPixel;

    // Rotation can make X/Y movement interact (a diagonal drag no longer
    // maps to an axis-aligned coverage boundary), so try the full move
    // first and fall back to sliding along just one axis if the other
    // would poke the photo's edge past the frame.
    const rot = totalRotationDeg();
    if (coversFrame(candidateX, candidateY, scale, rot)) {
      centerX = candidateX;
      centerY = candidateY;
    } else if (coversFrame(candidateX, centerY, scale, rot)) {
      centerX = candidateX;
    } else if (coversFrame(centerX, candidateY, scale, rot)) {
      centerY = candidateY;
    }
    applyTransform();
  });

  const endPointer = (e: PointerEvent) => {
    pointers.delete(e.pointerId);
    pinchStartDistance = 0;
    const remaining = [...pointers.values()][0];
    if (pointers.size === 1 && remaining) {
      dragStart = { centerX, centerY, x: remaining.x, y: remaining.y };
    } else {
      dragStart = undefined;
    }
  };
  stageEl.addEventListener('pointerup', endPointer);
  stageEl.addEventListener('pointercancel', endPointer);

  root.querySelector<HTMLButtonElement>('#cropCancelBtn')!.addEventListener('click', () => {
    actions.cancel();
  });

  root.querySelector<HTMLButtonElement>('#cropConfirmBtn')!.addEventListener('click', () => {
    const canvas = document.createElement('canvas');
    canvas.width = FRAME_WIDTH;
    canvas.height = FRAME_HEIGHT;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      actions.confirm(photo); // fall back to the uncropped photo rather than block the flow entirely
      return;
    }
    ctx.imageSmoothingQuality = 'high';
    // Replay the exact same centre/rotate/scale transform used on screen,
    // just shifted so the frame's top-left lands on the canvas's origin —
    // this is what makes it correct at any pan/zoom/rotation combination,
    // not just the axis-aligned case.
    ctx.translate(centerX - FRAME_X, centerY - FRAME_Y);
    ctx.rotate((totalRotationDeg() * Math.PI) / 180);
    ctx.scale(scale, scale);
    ctx.drawImage(photoEl, -photoNaturalW / 2, -photoNaturalH / 2);
    actions.confirm(canvas.toDataURL('image/jpeg', 0.9));
  });
}
